<template>
  <div class="operations-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ==================== -->
    <div class="op-header-row mb-5">
      <div class="op-header-titles">
        <div class="d-flex align-center gap-3 mb-1 flex-wrap">
          <h1 class="op-main-title d-flex align-center">
            <v-icon size="28" color="#082C69" class="me-2">mdi-account-group</v-icon>
            <span>السائقين</span>
          </h1>
        </div>

        <p class="op-subtitle">إدارة جميع السائقين ومراقبة أدائهم وحالتهم التشغيلية</p>
      </div>

      <!-- Header Actions -->
      <div class="op-header-actions d-flex align-center gap-2 flex-wrap">
        <!-- Add Driver Button -->
        <button
          type="button"
          class="op-btn-add-driver"
          @click="openAddDriverDialog"
        >
          <v-icon size="18" color="#FFFFFF" class="me-1">mdi-plus</v-icon>
          <span>إضافة سائق</span>
        </button>

        <button
          type="button"
          class="op-btn-refresh"
          :disabled="isLoading || tableLoading"
          @click="fetchDrivers"
        >
          <v-icon size="18" color="#082C69" class="me-1">mdi-refresh</v-icon>
          <span>تحديث</span>
        </button>
      </div>
    </div>

    <!-- ==================== 5 KPI CARDS GRID (ONE ROW ON DESKTOP) ==================== -->
    <div class="op-kpi-grid mb-5">
      <!-- 1. Total Drivers -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">إجمالي السائقين</div>
          <div class="kpi-value text-navy">{{ kpis.total_drivers }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
          <v-icon size="22" color="#082C69">mdi-account-tie</v-icon>
        </div>
      </div>

      <!-- 2. Active & Available -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">نشط ومتاح</div>
          <div class="kpi-value text-emerald">{{ kpis.active_drivers }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #DCFCE7; color: #16A34A;">
          <v-icon size="22" color="#16A34A">mdi-check-circle</v-icon>
        </div>
      </div>

      <!-- 3. Linked to Vehicle -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">مرتبط بمركبة</div>
          <div class="kpi-value text-blue">{{ kpis.with_vehicle }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #E0F2FE; color: #0284C7;">
          <v-icon size="22" color="#0284C7">mdi-bus</v-icon>
        </div>
      </div>

      <!-- 4. Without Vehicle -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">بدون مركبة</div>
          <div class="kpi-value text-amber">{{ kpis.without_vehicle }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #FEF3C7; color: #D97706;">
          <v-icon size="22" color="#D97706">mdi-car-off</v-icon>
        </div>
      </div>

      <!-- 5. Suspended / Inactive -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">غير نشط / موقوف</div>
          <div class="kpi-value text-rose">{{ kpis.inactive_suspended }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #FEE2E2; color: #DC2626;">
          <v-icon size="22" color="#DC2626">mdi-alert-circle</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== FILTER BAR ==================== -->
    <div class="op-filter-bar mb-5">
      <div class="filter-inputs-group">
        <!-- Search Field -->
        <div class="filter-search-box">
          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            placeholder="البحث بالاسم، الهاتف، أو لوحة المركبة..."
            outlined
            dense
            clearable
            hide-details
            class="op-search-input"
            @input="onSearchInput"
            @keydown.enter="onSearchEnter"
            @click:clear="onSearchClear"
          ></v-text-field>
        </div>

        <!-- Operational Status Filter -->
        <div class="filter-select-box">
          <v-select
            v-model="operationalStatusFilter"
            :items="operationalStatusOptions"
            label="حالة التشغيل"
            outlined
            dense
            hide-details
            class="op-filter-select"
            @change="fetchDrivers"
          ></v-select>
        </div>

        <!-- Vehicle Linkage Filter -->
        <div class="filter-select-box">
          <v-select
            v-model="vehicleStatusFilter"
            :items="vehicleStatusOptions"
            label="حالة الارتباط بالمركبة"
            outlined
            dense
            hide-details
            class="op-filter-select"
            @change="fetchDrivers"
          ></v-select>
        </div>

        <!-- Trip Group Filter (Scoped to assigned groups only) -->
        <div class="filter-select-box">
          <v-select
            v-model="tripGroupFilter"
            :items="tripGroupOptions"
            label="المجموعة"
            outlined
            dense
            hide-details
            class="op-filter-select"
            @change="fetchDrivers"
          ></v-select>
        </div>
      </div>

      <div class="filter-actions-group">
        <!-- Reset Button -->
        <button type="button" class="op-filter-btn" @click="resetFilters">
          <v-icon size="16" color="#63738A" class="me-1">mdi-restore</v-icon>
          <span>إعادة تعيين</span>
        </button>

        <!-- Advanced Filter Button -->
        <button type="button" class="op-filter-btn op-adv-filter-btn" title="المزيد من خيارات التصفية">
          <v-icon size="16" color="#082C69" class="me-1">mdi-tune-variant</v-icon>
          <span>فلترة متقدمة</span>
        </button>
      </div>
    </div>

    <!-- ==================== DRIVERS TABLE & PAGINATION ==================== -->
    <div class="op-table-card mb-5">
      <v-data-table
        :headers="headers"
        :items="driversList"
        :loading="tableLoading"
        :server-items-length="totalDrivers"
        :page.sync="page"
        :items-per-page.sync="perPage"
        :item-class="getTableRowClass"
        hide-default-footer
        class="op-data-table"
      >
        <!-- Driver Column: Avatar + Name + ID -->
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <div
              class="driver-avatar-circle me-3"
              :style="{ backgroundColor: getAvatarColor(item.name).bg, color: getAvatarColor(item.name).text }"
            >
              <img v-if="item.avatar" :src="item.avatar" :alt="item.name" class="driver-avatar-img" />
              <span v-else>{{ getInitials(item.name) }}</span>
            </div>
            <div>
              <div class="driver-name-text">{{ item.name }}</div>
              <div class="driver-id-caption">ID: #{{ item.id }}</div>
            </div>
          </div>
        </template>

        <!-- Phone Column -->
        <template v-slot:item.phone="{ item }">
          <div class="d-flex align-center phone-cell-content">
            <v-icon size="14" color="#63738A" class="me-1">mdi-phone-outline</v-icon>
            <span dir="ltr">{{ item.phone }}</span>
          </div>
        </template>

        <!-- Associated Vehicle Column -->
        <template v-slot:item.bus="{ item }">
          <div v-if="item.bus" class="d-flex align-center vehicle-cell-content">
            <v-icon size="16" color="#0284C7" class="me-2">mdi-bus</v-icon>
            <span class="vehicle-plate-text">{{ item.bus.license }}</span>
            <span class="vehicle-capacity-text ms-2">({{ item.bus.capacity }} راكب)</span>
          </div>
          <div v-else class="vehicle-unlinked-text">
            <v-icon size="15" color="#94A3B8" class="me-1">mdi-car-off</v-icon>
            <span>— غير مرتبط</span>
          </div>
        </template>

        <!-- Status Badge Column -->
        <template v-slot:item.badge="{ item }">
          <div class="op-status-badge" :style="getBadgeStyle(item)">
            <v-icon size="13" class="me-1" :color="getBadgeStyle(item).color">
              {{ getBadgeIcon(item.badge.status_key) }}
            </v-icon>
            <span>{{ item.badge.text }}</span>
          </div>
        </template>

        <!-- Rating Column -->
        <template v-slot:item.rating="{ item }">
          <div v-if="item.rating_average !== null" class="d-flex align-center rating-cell-content">
            <v-icon size="16" color="#F59E0B" class="me-1">mdi-star</v-icon>
            <span class="rating-avg-text">{{ item.rating_average }}</span>
            <span v-if="item.ratings_count" class="rating-count-text ms-1">({{ item.ratings_count }})</span>
          </div>
          <div v-else class="rating-empty-text">
            —
          </div>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left transition="slide-y-transition" @input="(val) => onMenuToggle(item.id, val)">
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="op-action-btn"
                :class="{ 'btn-active': activeMenuRowId === item.id }"
                v-bind="attrs"
                v-on="on"
                title="خيارات الإجراء"
              >
                <v-icon size="18" color="#63738A">mdi-dots-horizontal</v-icon>
              </button>
            </template>

            <v-list dense class="py-2 op-actions-menu">
              <!-- 1. View Details -->
              <v-list-item @click="openDetailsModal(item)">
                <v-list-item-icon class="me-3">
                  <v-icon size="18" color="#082C69">mdi-eye-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-bold text-navy">عرض التفاصيل</v-list-item-title>
              </v-list-item>

              <!-- 2. Assign Vehicle (when driver has NO vehicle) -->
              <v-list-item v-if="!item.bus" @click="openAssignVehicleModal(item)">
                <v-list-item-icon class="me-3">
                  <v-icon size="18" color="#10B981">mdi-bus-plus</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-bold text-emerald">تعيين مركبة</v-list-item-title>
              </v-list-item>

              <!-- 3. Change Vehicle (when driver HAS vehicle) -->
              <v-list-item v-if="item.bus" @click="openAssignVehicleModal(item)">
                <v-list-item-icon class="me-3">
                  <v-icon size="18" color="#0284C7">mdi-swap-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-bold text-blue">تغيير المركبة</v-list-item-title>
              </v-list-item>

              <!-- 4. Operational History (Scoped) -->
              <v-list-item @click="openHistoryModal(item)">
                <v-list-item-icon class="me-3">
                  <v-icon size="18" color="#082C69">mdi-history</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-bold text-navy">سجل التشغيل</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1"></v-divider>

              <!-- 5. Suspend Driver (when active) -->
              <v-list-item v-if="item.status_id === 1" @click="openSuspendModal(item)">
                <v-list-item-icon class="me-3">
                  <v-icon size="18" color="#EF4444">mdi-pause-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-bold text-rose">إيقاف السائق</v-list-item-title>
              </v-list-item>

              <!-- 6. Reactivate Driver (when suspended) -->
              <v-list-item v-if="item.status_id === 3" @click="openReactivateModal(item)">
                <v-list-item-icon class="me-3">
                  <v-icon size="18" color="#10B981">mdi-play-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-bold text-emerald">إعادة تفعيل السائق</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="py-12 text-center">
            <v-icon size="48" color="#94A3B8" class="mb-3">mdi-account-search-outline</v-icon>
            <div class="font-weight-bold text-navy mb-1" style="font-size: 16px;">لا توجد نتائج مطابقة</div>
            <div class="caption text-muted">يرجى تعديل مصطلحات البحث أو الفلاتر المختارة</div>
          </div>
        </template>
      </v-data-table>

      <!-- Custom Pagination Bar (Matches Reference Screenshot) -->
      <div class="op-pagination-footer">
        <!-- Right (RTL): Rows count and selector -->
        <div class="d-flex align-center gap-3 flex-wrap">
          <div class="d-flex align-center gap-2">
            <span class="pagination-label">عرض</span>
            <select v-model="perPage" class="op-rows-select" @change="onPerPageChange(perPage)">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>
          </div>
          <span class="pagination-info">{{ paginationText }}</span>
        </div>

        <!-- Left (RTL): Navigation arrows and page numbers -->
        <div class="d-flex align-center gap-1">
          <button
            type="button"
            class="op-page-nav-btn"
            :disabled="page <= 1"
            title="الصفحة السابقة"
            @click="onPageChange(page - 1)"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>

          <template v-for="(p, idx) in visiblePages">
            <span v-if="p === '...'" :key="'ellipsis-' + idx" class="op-page-ellipsis">...</span>
            <button
              v-else
              :key="'page-' + p"
              type="button"
              class="op-page-number-btn"
              :class="{ active: p === page }"
              @click="onPageChange(p)"
            >
              {{ p }}
            </button>
          </template>

          <button
            type="button"
            class="op-page-nav-btn"
            :disabled="page >= totalPages"
            title="الصفحة التالية"
            @click="onPageChange(page + 1)"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== MODAL 1: DRIVER DETAILS ==================== -->
    <v-dialog
      v-model="detailsModalOpen"
      max-width="1180"
      persistent
      content-class="op-details-dialog"
    >
      <v-card v-if="selectedDriverDetails" class="op-dialog-card d-flex flex-column" style="max-height: 90vh;">
        <!-- Fixed Modal Header -->
        <div class="op-dialog-header d-flex align-center justify-space-between px-6 py-4">
          <div class="d-flex align-center">
            <div
              class="driver-avatar-circle me-3"
              :style="{ backgroundColor: getAvatarColor(selectedDriverDetails.name).bg, color: getAvatarColor(selectedDriverDetails.name).text, width: '46px', height: '46px', fontSize: '16px' }"
            >
              <img v-if="selectedDriverDetails.avatar" :src="selectedDriverDetails.avatar" :alt="selectedDriverDetails.name" class="driver-avatar-img" />
              <span v-else>{{ getInitials(selectedDriverDetails.name) }}</span>
            </div>
            <div>
              <div class="font-weight-bold text-navy" style="font-size: 19px; line-height: 1.2;">
                تفاصيل السائق — {{ selectedDriverDetails.name }}
              </div>
              <div class="caption text-muted mt-1">تفاصيل السائق والنشاط التشغيلي المسند</div>
            </div>
          </div>
          <v-btn icon small @click="detailsModalOpen = false" class="close-modal-btn">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Fixed Tabs Bar -->
        <div class="op-modal-tabs-wrapper px-6">
          <v-tabs v-model="detailsTab" slider-color="#082C69" class="op-modal-tabs">
            <v-tab>
              <v-icon size="18" class="me-2" color="#082C69">mdi-account-details-outline</v-icon>
              <span>البيانات الأساسية</span>
            </v-tab>
            <v-tab>
              <v-icon size="18" class="me-2" color="#082C69">mdi-bus</v-icon>
              <span>بيانات المركبة</span>
            </v-tab>
            <v-tab>
              <v-icon size="18" class="me-2" color="#082C69">mdi-file-document-outline</v-icon>
              <span>المستندات</span>
            </v-tab>
            <v-tab>
              <v-icon size="18" class="me-2" color="#082C69">mdi-clock-outline</v-icon>
              <span>سجل التشغيل</span>
            </v-tab>
          </v-tabs>
        </div>

        <!-- Scrollable Modal Body -->
        <div class="op-modal-scroll-body px-6 py-4 flex-grow-1 overflow-y-auto">
          <v-tabs-items v-model="detailsTab">
            <!-- ==================== TAB 1: البيانات الأساسية ==================== -->
            <v-tab-item class="pa-1">
              <!-- Profile Hero Card -->
              <div class="profile-hero-card mb-4">
                <!-- Right side: Driver Profile Info -->
                <div class="profile-main-info d-flex align-center">
                  <div
                    class="profile-hero-avatar me-4"
                    :style="{ backgroundColor: getAvatarColor(selectedDriverDetails.name).bg, color: getAvatarColor(selectedDriverDetails.name).text }"
                  >
                    <img v-if="selectedDriverDetails.avatar" :src="selectedDriverDetails.avatar" :alt="selectedDriverDetails.name" />
                    <span v-else>{{ getInitials(selectedDriverDetails.name) }}</span>
                  </div>
                  <div>
                    <h2 class="profile-hero-name text-navy">{{ selectedDriverDetails.name }}</h2>
                    <div class="profile-hero-id mb-2">رقم السائق : #{{ String(selectedDriverDetails.id).padStart(3, '0') }}</div>
                    <div class="d-flex align-center flex-wrap gap-4 text-muted caption">
                      <div class="d-flex align-center">
                        <v-icon size="15" color="#64748B" class="me-1">mdi-phone-outline</v-icon>
                        <span dir="ltr" class="font-weight-bold text-navy">{{ selectedDriverDetails.phone }}</span>
                      </div>
                      <div class="d-flex align-center ms-4">
                        <v-icon size="15" color="#64748B" class="me-1">mdi-email-outline</v-icon>
                        <span class="font-weight-bold text-navy">{{ selectedDriverDetails.email }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Left side: Operational Profile Meta Specs -->
                <div class="profile-meta-specs">
                  <div class="meta-spec-item">
                    <v-icon size="18" color="#082C69" class="me-2">mdi-account-tie</v-icon>
                    <span class="spec-label">نوع السائق:</span>
                    <strong class="spec-val text-navy">{{ selectedDriverDetails.driver_type_label }}</strong>
                  </div>
                  <div class="meta-spec-item">
                    <v-icon size="18" color="#082C69" class="me-2">mdi-bus</v-icon>
                    <span class="spec-label">نوع المركبة:</span>
                    <strong class="spec-val text-navy">{{ selectedDriverDetails.bus ? selectedDriverDetails.bus.type : '—' }}</strong>
                  </div>
                  <div class="meta-spec-item">
                    <v-icon size="18" color="#082C69" class="me-2">mdi-card-text-outline</v-icon>
                    <span class="spec-label">اللوحة الحالية:</span>
                    <strong class="spec-val text-navy">{{ selectedDriverDetails.bus ? selectedDriverDetails.bus.license : '—' }}</strong>
                  </div>
                  <div class="meta-spec-item">
                    <v-icon size="18" color="#082C69" class="me-2">mdi-seat-passenger</v-icon>
                    <span class="spec-label">السعة:</span>
                    <strong class="spec-val text-navy">{{ selectedDriverDetails.bus ? selectedDriverDetails.bus.capacity + ' راكب' : '—' }}</strong>
                  </div>
                </div>
              </div>

              <!-- 4 Information Cards Grid -->
              <v-row dense>
                <!-- Card 1: المعلومات الشخصية -->
                <v-col cols="12" md="6">
                  <div class="op-info-card">
                    <div class="op-card-header">
                      <v-icon size="20" color="#082C69" class="me-2">mdi-account-circle-outline</v-icon>
                      <span class="op-card-title">المعلومات الشخصية</span>
                    </div>
                    <div class="op-card-content">
                      <div class="info-grid-row">
                        <span class="info-grid-label">الاسم الكامل</span>
                        <span class="info-grid-value text-navy">{{ selectedDriverDetails.name }}</span>
                      </div>
                      <div class="info-grid-row">
                        <span class="info-grid-label">رقم الهوية</span>
                        <span class="info-grid-value text-navy">{{ selectedDriverDetails.national_id }}</span>
                      </div>
                      <div class="info-grid-row">
                        <span class="info-grid-label">تاريخ الميلاد</span>
                        <span class="info-grid-value text-navy">{{ selectedDriverDetails.birth_date }}</span>
                      </div>
                      <div class="info-grid-row" style="border-bottom: none;">
                        <span class="info-grid-label">نوع السائق</span>
                        <span class="info-grid-value font-weight-bold text-navy">{{ selectedDriverDetails.driver_type_label }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Card 2: معلومات التواصل -->
                <v-col cols="12" md="6">
                  <div class="op-info-card">
                    <div class="op-card-header">
                      <v-icon size="20" color="#082C69" class="me-2">mdi-phone-in-talk-outline</v-icon>
                      <span class="op-card-title">معلومات التواصل</span>
                    </div>
                    <div class="op-card-content">
                      <div class="info-grid-row">
                        <span class="info-grid-label">رقم الهاتف</span>
                        <span class="info-grid-value text-navy" dir="ltr">{{ selectedDriverDetails.phone }}</span>
                      </div>
                      <div class="info-grid-row" style="border-bottom: none;">
                        <span class="info-grid-label">البريد الإلكتروني</span>
                        <span class="info-grid-value text-navy">{{ selectedDriverDetails.email }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Card 3: معلومات التوظيف -->
                <v-col cols="12" md="6" class="mt-4">
                  <div class="op-info-card">
                    <div class="op-card-header">
                      <v-icon size="20" color="#082C69" class="me-2">mdi-briefcase-outline</v-icon>
                      <span class="op-card-title">معلومات التوظيف</span>
                    </div>
                    <div class="op-card-content">
                      <div class="info-grid-row">
                        <span class="info-grid-label">تاريخ الانضمام</span>
                        <span class="info-grid-value text-navy">{{ selectedDriverDetails.joined_at }}</span>
                      </div>
                      <div class="info-grid-row" style="border-bottom: none;">
                        <span class="info-grid-label">حالة العمل</span>
                        <span class="info-grid-value font-weight-bold text-emerald">
                          {{ selectedDriverDetails.working_status === 'active' ? 'نشط بالمنظومة' : 'غير نشط' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Card 4: حالة السائق التشغيلية -->
                <v-col cols="12" md="6" class="mt-4">
                  <div class="op-info-card">
                    <div class="op-card-header">
                      <v-icon size="20" color="#082C69" class="me-2">mdi-account-check-outline</v-icon>
                      <span class="op-card-title">حالة السائق التشغيلية</span>
                    </div>
                    <div class="op-card-content">
                      <div class="info-grid-row">
                        <span class="info-grid-label">الحالة التشغيلية</span>
                        <div class="op-status-badge d-inline-flex" :style="getBadgeStyle(selectedDriverDetails)">
                          <v-icon size="13" class="me-1" :color="getBadgeStyle(selectedDriverDetails).color">
                            {{ getBadgeIcon(selectedDriverDetails.badge.status_key) }}
                          </v-icon>
                          <span>{{ selectedDriverDetails.badge.text }}</span>
                        </div>
                      </div>
                      <div class="info-grid-row">
                        <span class="info-grid-label">مرتبط بمركبة</span>
                        <span class="info-grid-value font-weight-bold" :class="selectedDriverDetails.bus ? 'text-emerald' : 'text-amber'">
                          {{ selectedDriverDetails.bus ? 'نعم' : 'لا' }}
                        </span>
                      </div>
                      <div class="info-grid-row" style="border-bottom: none;">
                        <span class="info-grid-label">اللوحة الحالية</span>
                        <span class="info-grid-value font-weight-bold text-navy">
                          {{ selectedDriverDetails.bus ? selectedDriverDetails.bus.license : '— غير مرتبط' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- ==================== TAB 2: بيانات المركبة ==================== -->
            <v-tab-item class="pa-1">
              <!-- 5 Top KPI Cards -->
              <div class="op-sub-kpi-grid mb-4">
                <!-- 1. Assignment status -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">حالة التخصيص الحالية</div>
                    <div class="font-weight-bold" :class="selectedDriverDetails.bus ? 'text-emerald' : 'text-amber'" style="font-size: 16px;">
                      {{ selectedDriverDetails.bus ? 'نشط' : 'غير معين' }}
                    </div>
                    <div class="caption text-muted mt-1">{{ selectedDriverDetails.bus ? 'معين على مركبة' : 'بدون مركبة' }}</div>
                  </div>
                  <div class="kpi-icon-side" :style="{ backgroundColor: selectedDriverDetails.bus ? '#DCFCE7' : '#FEF3C7', color: selectedDriverDetails.bus ? '#16A34A' : '#D97706' }">
                    <v-icon size="20" :color="selectedDriverDetails.bus ? '#16A34A' : '#D97706'">
                      {{ selectedDriverDetails.bus ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                    </v-icon>
                  </div>
                </div>

                <!-- 2. Current Vehicle -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">المركبة الحالية</div>
                    <div class="font-weight-bold text-navy" style="font-size: 15px;">
                      {{ selectedDriverDetails.bus ? selectedDriverDetails.bus.type : 'لا توجد' }}
                    </div>
                    <div class="caption text-muted mt-1">{{ selectedDriverDetails.bus ? selectedDriverDetails.bus.capacity + ' راكب' : '—' }}</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #E0F2FE; color: #0284C7;">
                    <v-icon size="20" color="#0284C7">mdi-bus</v-icon>
                  </div>
                </div>

                <!-- 3. Plate Number -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">رقم اللوحة</div>
                    <div class="font-weight-bold text-navy" style="font-size: 16px;">
                      {{ selectedDriverDetails.bus ? selectedDriverDetails.bus.license : '—' }}
                    </div>
                    <div class="caption text-muted mt-1">جمهورية مصر العربية</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
                    <v-icon size="20" color="#082C69">mdi-card-text-outline</v-icon>
                  </div>
                </div>

                <!-- 4. Vehicle Ownership -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">ملكية المركبة</div>
                    <div class="font-weight-bold text-navy" style="font-size: 15px;">
                      {{ selectedDriverDetails.vehicle_ownership }}
                    </div>
                    <div class="caption text-muted mt-1">حسب تسجيل السائق</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
                    <v-icon size="20" color="#082C69">mdi-domain</v-icon>
                  </div>
                </div>

                <!-- 5. License Expiry -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">انتهاء رخصة المركبة</div>
                    <div class="font-weight-bold text-navy" style="font-size: 14px;">
                      {{ selectedDriverDetails.bus && selectedDriverDetails.bus.license_expiry_date ? selectedDriverDetails.bus.license_expiry_date : '—' }}
                    </div>
                    <div class="caption text-muted mt-1">
                      <span v-if="selectedDriverDetails.bus && selectedDriverDetails.bus.days_until_license_expiry !== null">
                        متبقي {{ selectedDriverDetails.bus.days_until_license_expiry }} يوم
                      </span>
                      <span v-else>غير محدد</span>
                    </div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #FFF3D6; color: #D97706;">
                    <v-icon size="20" color="#D97706">mdi-calendar-clock</v-icon>
                  </div>
                </div>
              </div>

              <!-- Large Current Vehicle Card -->
              <div class="op-current-vehicle-card mb-5">
                <div class="d-flex align-center justify-space-between mb-4">
                  <div class="d-flex align-center">
                    <v-icon size="22" color="#082C69" class="me-2">mdi-bus-side</v-icon>
                    <span class="font-weight-bold text-navy" style="font-size: 17px;">المركبة الحالية</span>
                  </div>
                </div>

                <div v-if="selectedDriverDetails.bus">
                  <v-row align="center">
                    <!-- Vehicle Image Preview -->
                    <v-col cols="12" md="4" class="text-center">
                      <div class="vehicle-img-box">
                        <img src="/img/generic_van.svg" alt="Vehicle" style="max-height: 140px; width: auto;" />
                        <div class="font-weight-bold text-navy mt-2" style="font-size: 15px;">
                          {{ selectedDriverDetails.bus.type }}
                        </div>
                        <div class="caption text-muted">سعة {{ selectedDriverDetails.bus.capacity }} مقعد</div>
                      </div>
                    </v-col>

                    <!-- Vehicle Details Grid -->
                    <v-col cols="12" md="8">
                      <div class="vehicle-specs-grid">
                        <div class="spec-cell">
                          <span class="detail-label">نوع المركبة</span>
                          <div class="detail-val">{{ selectedDriverDetails.bus.type }}</div>
                        </div>
                        <div class="spec-cell">
                          <span class="detail-label">رقم اللوحة</span>
                          <div class="detail-val font-weight-bold">{{ selectedDriverDetails.bus.license }}</div>
                        </div>
                        <div class="spec-cell">
                          <span class="detail-label">ملكية المركبة</span>
                          <div class="detail-val text-navy">{{ selectedDriverDetails.vehicle_ownership }}</div>
                        </div>
                        <div class="spec-cell">
                          <span class="detail-label">فئة المقاعد</span>
                          <div class="detail-val">{{ selectedDriverDetails.bus.capacity }} مقعد</div>
                        </div>
                        <div class="spec-cell">
                          <span class="detail-label">تاريخ بدء التخصيص</span>
                          <div class="detail-val">{{ selectedDriverDetails.bus.assigned_at }}</div>
                        </div>
                        <div class="spec-cell">
                          <span class="detail-label">تاريخ انتهاء الترخيص</span>
                          <div class="detail-val">{{ selectedDriverDetails.bus.license_expiry_date }}</div>
                        </div>
                      </div>

                      <!-- Vehicle Actions -->
                      <div class="d-flex align-center gap-3 mt-4 pt-3 border-top">
                        <v-btn
                          depressed
                          color="#082C69"
                          class="white--text font-weight-bold me-3"
                          @click="openAssignFromDetails"
                        >
                          <v-icon size="18" class="me-1">mdi-swap-horizontal</v-icon>
                          <span>تغيير المركبة</span>
                        </v-btn>

                        <v-btn
                          depressed
                          color="#FEE2E2"
                          class="text-rose font-weight-bold"
                          :loading="isSubmittingVehicle"
                          @click="unassignFromDetails"
                        >
                          <v-icon size="18" color="#DC2626" class="me-1">mdi-link-variant-off</v-icon>
                          <span>إلغاء التخصيص</span>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Empty Vehicle State -->
                <div v-else class="text-center py-8 text-muted">
                  <v-icon size="56" color="#94A3B8" class="mb-3">mdi-car-off</v-icon>
                  <div class="font-weight-bold text-navy" style="font-size: 16px;">هذا السائق غير مرتبط بأي مركبة حالياً</div>
                  <div class="caption text-muted mt-1 mb-4">يمكنك تخصيص مركبة شاغرة ومتاحة بالأسطول للسائق مباشرة</div>
                  <v-btn
                    depressed
                    color="#082C69"
                    class="white--text font-weight-bold"
                    @click="openAssignFromDetails"
                  >
                    <v-icon size="18" class="me-1">mdi-bus-plus</v-icon>
                    <span>تعيين مركبة الآن</span>
                  </v-btn>
                </div>
              </div>

              <!-- Vehicle Assignment History Table -->
              <div class="op-info-card">
                <div class="op-card-header justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon size="20" color="#082C69" class="me-2">mdi-history</v-icon>
                    <span class="op-card-title">سجل المركبات المرتبطة بالسائق</span>
                  </div>
                  <span class="caption text-muted">إجمالي السجلات: {{ selectedDriverDetails.vehicle_assignments ? selectedDriverDetails.vehicle_assignments.length : 0 }}</span>
                </div>

                <div class="op-modal-table">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-right">#</th>
                          <th class="text-right">المركبة</th>
                          <th class="text-right">اللوحة</th>
                          <th class="text-right">ملكية المركبة</th>
                          <th class="text-right">من</th>
                          <th class="text-right">إلى</th>
                          <th class="text-center">الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in selectedDriverDetails.vehicle_assignments" :key="item.id">
                          <td class="font-weight-bold text-navy">{{ idx + 1 }}</td>
                          <td>{{ item.bus_type }}</td>
                          <td class="font-weight-bold text-navy">{{ item.bus_license }}</td>
                          <td>{{ item.ownership }}</td>
                          <td>{{ item.started_at }}</td>
                          <td>{{ item.ended_at || '—' }}</td>
                          <td class="text-center">
                            <span
                              class="op-status-badge d-inline-block"
                              :style="{
                                backgroundColor: item.status === 'active' ? '#DCFCE7' : '#EEF1F5',
                                color: item.status === 'active' ? '#15803D' : '#596579',
                                borderColor: item.status === 'active' ? '#BBF7D0' : '#CBD5E1'
                              }"
                            >
                              {{ item.status_label }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="!selectedDriverDetails.vehicle_assignments || selectedDriverDetails.vehicle_assignments.length === 0">
                          <td colspan="7" class="text-center py-6 text-muted">
                            لا يوجد سجل تعيين مركبات سابق لهذا السائق.
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </div>
            </v-tab-item>

            <!-- ==================== TAB 3: المستندات ==================== -->
            <v-tab-item class="pa-1">
              <!-- 4 Top KPI Cards -->
              <div class="op-sub-kpi-grid mb-4" style="grid-template-columns: repeat(4, 1fr);">
                <!-- 1. Total Docs -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">إجمالي المستندات</div>
                    <div class="kpi-value text-navy">{{ selectedDriverDetails.document_kpis.total }}</div>
                    <div class="caption text-muted mt-1">مستندات مسجلة</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
                    <v-icon size="20" color="#082C69">mdi-file-document-multiple-outline</v-icon>
                  </div>
                </div>

                <!-- 2. Completed -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">مكتمل</div>
                    <div class="kpi-value text-emerald">{{ selectedDriverDetails.document_kpis.completed }}</div>
                    <div class="caption text-muted mt-1">معتمد وساري</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #DCFCE7; color: #16A34A;">
                    <v-icon size="20" color="#16A34A">mdi-check-circle-outline</v-icon>
                  </div>
                </div>

                <!-- 3. Expiring Soon -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">قارب الانتهاء</div>
                    <div class="kpi-value text-amber">{{ selectedDriverDetails.document_kpis.near_expiry }}</div>
                    <div class="caption text-muted mt-1">خلال 30 يوماً</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #FFF3D6; color: #D97706;">
                    <v-icon size="20" color="#D97706">mdi-clock-alert-outline</v-icon>
                  </div>
                </div>

                <!-- 4. Needs Update -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">مطلوب تحديث</div>
                    <div class="kpi-value text-rose">{{ selectedDriverDetails.document_kpis.needs_update }}</div>
                    <div class="caption text-muted mt-1">منتهي أو غير مكتمل</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #FEE2E2; color: #DC2626;">
                    <v-icon size="20" color="#DC2626">mdi-alert-circle-outline</v-icon>
                  </div>
                </div>
              </div>

              <!-- Documents Grid -->
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <v-icon size="20" color="#082C69" class="me-2">mdi-folder-file-outline</v-icon>
                  <span class="font-weight-bold text-navy" style="font-size: 16px;">قائمة المستندات</span>
                </div>
              </div>

              <div class="doc-cards-grid">
                <div
                  v-for="doc in selectedDriverDetails.documents"
                  :key="doc.id"
                  class="op-doc-card"
                >
                  <div class="doc-card-header">
                    <span class="font-weight-bold text-navy" style="font-size: 14.5px;">{{ doc.document_name }}</span>
                    <v-icon size="20" color="#082C69">{{ getDocIcon(doc.document_name) }}</v-icon>
                  </div>

                  <div class="doc-card-body">
                    <div class="doc-card-thumb">
                      <v-icon size="28" color="#64748B">{{ getDocIcon(doc.document_name) }}</v-icon>
                    </div>
                    <div class="flex-grow-1">
                      <div class="detail-label">رقم المستند</div>
                      <div class="font-weight-bold text-navy mb-2" style="font-size: 13.5px;">{{ doc.document_number }}</div>

                      <div class="d-flex align-center justify-space-between caption text-muted mb-1">
                        <span>تاريخ الإصدار:</span>
                        <span class="font-weight-medium text-navy">{{ doc.issue_date }}</span>
                      </div>

                      <div class="d-flex align-center justify-space-between caption text-muted mb-2">
                        <span>تاريخ الانتهاء:</span>
                        <span class="font-weight-medium text-navy">{{ doc.expiry_date }}</span>
                      </div>

                      <div>
                        <span
                          class="op-status-badge"
                          :style="{
                            backgroundColor: getDocBadgeBg(doc.status_key),
                            color: getDocBadgeColor(doc.status_key),
                            borderColor: getDocBadgeColor(doc.status_key) + '44'
                          }"
                        >
                          {{ doc.status_label }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="doc-actions-row">
                    <button
                      type="button"
                      class="doc-action-btn"
                      :disabled="!doc.has_file"
                      @click="previewDocument(doc)"
                    >
                      <v-icon size="14" color="#082C69" class="me-1">mdi-eye-outline</v-icon>
                      <span>عرض</span>
                    </button>

                    <button
                      type="button"
                      class="doc-action-btn"
                      :disabled="!doc.has_file"
                      @click="downloadDocument(doc)"
                    >
                      <v-icon size="14" color="#082C69" class="me-1">mdi-download-outline</v-icon>
                      <span>تحميل</span>
                    </button>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- ==================== TAB 4: سجل التشغيل ==================== -->
            <v-tab-item class="pa-1">
              <!-- 5 Top KPI Cards -->
              <div class="op-sub-kpi-grid mb-4">
                <!-- 1. Total Trips -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">إجمالي الرحلات</div>
                    <div class="kpi-value text-navy">{{ selectedDriverDetails.operational_summary.total_trips }}</div>
                    <div class="caption text-muted mt-1">ضمن نطاق المجموعة</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
                    <v-icon size="20" color="#082C69">mdi-bus-clock</v-icon>
                  </div>
                </div>

                <!-- 2. Avg Rating -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">متوسط التقييم</div>
                    <div class="kpi-value text-navy">
                      <span v-if="selectedDriverDetails.operational_summary.rating_average !== '—'">
                        ⭐ {{ selectedDriverDetails.operational_summary.rating_average }}
                      </span>
                      <span v-else>—</span>
                    </div>
                    <div class="caption text-muted mt-1">تقييم العملاء</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #FFF3D6; color: #F59E0B;">
                    <v-icon size="20" color="#F59E0B">mdi-star</v-icon>
                  </div>
                </div>

                <!-- 3. Cancelled Trips -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">الرحلات الملغاة</div>
                    <div class="kpi-value text-rose">{{ selectedDriverDetails.operational_summary.cancelled_trips }}</div>
                    <div class="caption text-muted mt-1">رحلات ملغاة</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #FEE2E2; color: #DC2626;">
                    <v-icon size="20" color="#DC2626">mdi-close-circle-outline</v-icon>
                  </div>
                </div>

                <!-- 4. Completed Trips -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">الرحلات المكتملة</div>
                    <div class="kpi-value text-emerald">{{ selectedDriverDetails.operational_summary.completed_trips }}</div>
                    <div class="caption text-muted mt-1">تم تنفيذها بنجاح</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #DCFCE7; color: #16A34A;">
                    <v-icon size="20" color="#16A34A">mdi-check-circle-outline</v-icon>
                  </div>
                </div>

                <!-- 5. Compliance Rate -->
                <div class="op-sub-kpi-card">
                  <div>
                    <div class="kpi-label">نسبة الالتزام</div>
                    <div class="kpi-value text-navy">{{ selectedDriverDetails.operational_summary.compliance_rate }}</div>
                    <div class="caption text-muted mt-1">من إجمالي الرحلات</div>
                  </div>
                  <div class="kpi-icon-side" style="background-color: #DCFCE7; color: #16A34A;">
                    <v-icon size="20" color="#16A34A">mdi-progress-check</v-icon>
                  </div>
                </div>
              </div>

              <!-- Section 1: Recent Trips Table -->
              <div class="op-info-card mb-4">
                <div class="op-card-header justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon size="20" color="#082C69" class="me-2">mdi-bus-side</v-icon>
                    <span class="op-card-title">تفاصيل الرحلات الأخيرة</span>
                  </div>
                </div>

                <div class="op-modal-table">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-right">#</th>
                          <th class="text-right">رقم الرحلة</th>
                          <th class="text-right">التاريخ</th>
                          <th class="text-right">المسار</th>
                          <th class="text-right">وقت البداية</th>
                          <th class="text-right">وقت النهاية</th>
                          <th class="text-right">المركبة / اللوحة</th>
                          <th class="text-center">الحجوزات</th>
                          <th class="text-center">الركاب الفعليين</th>
                          <th class="text-center">الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(t, idx) in selectedDriverDetails.operational_summary.recent_trips" :key="t.id">
                          <td class="font-weight-bold text-navy">{{ idx + 1 }}</td>
                          <td class="font-weight-bold text-navy">{{ t.planned_trip_reference }}</td>
                          <td>{{ t.planned_date }}</td>
                          <td>{{ t.route_name }}</td>
                          <td>{{ t.start_time }}</td>
                          <td>{{ t.end_time }}</td>
                          <td class="font-weight-medium text-navy">{{ t.bus_license }}</td>
                          <td class="text-center">{{ t.reservations_count }}</td>
                          <td class="text-center font-weight-bold text-navy">{{ t.actual_passengers }}</td>
                          <td class="text-center">
                            <span
                              class="op-status-badge d-inline-block"
                              :style="{
                                backgroundColor: getBadgeBgColor(t.status_color),
                                color: getBadgeTextColor(t.status_color),
                                borderColor: getBadgeBorderColor(t.status_color)
                              }"
                            >
                              {{ t.status_label }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="!selectedDriverDetails.operational_summary.recent_trips || selectedDriverDetails.operational_summary.recent_trips.length === 0">
                          <td colspan="10" class="text-center py-8 text-muted">
                            <v-icon size="40" color="#94A3B8" class="mb-2">mdi-bus-stop</v-icon>
                            <div class="font-weight-bold text-navy">لا توجد رحلات منفذة ضمن نطاق مجموعات التشغيل المسندة إليك</div>
                            <div class="caption">لم يقم السائق بتشغيل أي رحلات في المجموعات المخصصة لك حتى الآن.</div>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </div>

              <!-- Section 2: Routes Worked Table -->
              <div class="op-info-card mb-4">
                <div class="op-card-header justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon size="20" color="#082C69" class="me-2">mdi-routes</v-icon>
                    <span class="op-card-title">الخطوط التي عمل عليها</span>
                  </div>
                  <span class="caption text-muted">إجمالي الخطوط: {{ selectedDriverDetails.operational_summary.routes_worked ? selectedDriverDetails.operational_summary.routes_worked.length : 0 }}</span>
                </div>

                <div class="op-modal-table">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-right">#</th>
                          <th class="text-right">اسم الخط</th>
                          <th class="text-center">الرحلات المكتملة</th>
                          <th class="text-center">إجمالي الحجوزات</th>
                          <th class="text-center">إجمالي الركاب</th>
                          <th class="text-center">نسبة الالتزام</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(r, idx) in selectedDriverDetails.operational_summary.routes_worked" :key="idx">
                          <td class="font-weight-bold text-navy">{{ idx + 1 }}</td>
                          <td class="font-weight-bold text-navy">{{ r.route_name }}</td>
                          <td class="text-center">{{ r.completed_trips }}</td>
                          <td class="text-center">{{ r.total_reservations }}</td>
                          <td class="text-center font-weight-bold text-navy">{{ r.total_passengers }}</td>
                          <td class="text-center">
                            <v-chip x-small color="#DCFCE7" text-color="#15803D" class="font-weight-bold">
                              {{ r.compliance_rate }}
                            </v-chip>
                          </td>
                        </tr>
                        <tr v-if="!selectedDriverDetails.operational_summary.routes_worked || selectedDriverDetails.operational_summary.routes_worked.length === 0">
                          <td colspan="6" class="text-center py-6 text-muted">
                            لا توجد خطوط تشغيلية مسجلة ضمن نطاق مجموعاتك المسندة.
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </div>

              <!-- Section 3: Event Log Table -->
              <div class="op-info-card">
                <div class="op-card-header justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon size="20" color="#082C69" class="me-2">mdi-format-list-bulleted-type</v-icon>
                    <span class="op-card-title">سجل الأحداث</span>
                  </div>
                  <span class="caption text-muted">سجل التشغيل المباشر</span>
                </div>

                <div class="op-modal-table">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-right">#</th>
                          <th class="text-right">التاريخ</th>
                          <th class="text-right">النوع</th>
                          <th class="text-right">الوصف</th>
                          <th class="text-center">الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(ev, idx) in selectedDriverDetails.operational_summary.event_logs" :key="idx">
                          <td class="font-weight-bold text-navy">{{ idx + 1 }}</td>
                          <td>{{ ev.date }}</td>
                          <td class="font-weight-bold text-navy">{{ ev.type }}</td>
                          <td>{{ ev.description }}</td>
                          <td class="text-center">
                            <v-chip x-small :color="ev.status_color === 'success' ? '#DCFCE7' : '#F1F5F9'" :text-color="ev.status_color === 'success' ? '#15803D' : '#64748B'" class="font-weight-bold">
                              {{ ev.status }}
                            </v-chip>
                          </td>
                        </tr>
                        <tr v-if="!selectedDriverDetails.operational_summary.event_logs || selectedDriverDetails.operational_summary.event_logs.length === 0">
                          <td colspan="5" class="text-center py-6 text-muted">
                            لا توجد أحداث تشغيلية مسجلة حديثاً.
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>

        <!-- Fixed Modal Footer -->
        <div class="op-dialog-footer px-6 py-3 d-flex align-center justify-end">
          <v-btn depressed color="#64748B" class="white--text font-weight-bold" @click="detailsModalOpen = false">
            إغلاق
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Document Preview Dialog -->
    <v-dialog v-model="docPreviewModalOpen" max-width="600">
      <v-card class="op-dialog-card">
        <v-card-title class="d-flex align-center justify-space-between pb-2 op-dialog-header">
          <span class="font-weight-bold text-navy">{{ activePreviewDoc ? activePreviewDoc.document_name : 'معاينة المستند' }}</span>
          <v-btn icon small @click="docPreviewModalOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4 text-center">
          <img v-if="activePreviewDoc && activePreviewDoc.file_url" :src="activePreviewDoc.file_url" class="img-fluid" style="max-height: 450px; border-radius: 8px;" />
          <div v-else class="py-8 text-muted">
            <v-icon size="48" color="#94A3B8" class="mb-2">mdi-file-eye-outline</v-icon>
            <div>لا يوجد ملف معاينة متاح لهذا المستند</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ==================== MODAL 2: ASSIGN / CHANGE VEHICLE ==================== -->
    <v-dialog v-model="assignModalOpen" max-width="500" persistent>
      <v-card v-if="driverForVehicle" class="op-dialog-card">
        <v-card-title class="d-flex align-center justify-space-between pb-3 op-dialog-header">
          <div class="font-weight-bold text-navy" style="font-size: 17px;">
            {{ driverForVehicle.bus ? 'تغيير مركبة السائق' : 'تعيين مركبة للسائق' }}
          </div>
          <v-btn icon small @click="assignModalOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="mb-4">
            <div class="detail-label">السائق المحدد</div>
            <div class="font-weight-bold text-navy subtitle-1">{{ driverForVehicle.name }}</div>
          </div>

          <!-- Current Vehicle Indicator -->
          <div v-if="driverForVehicle.bus" class="mb-4 pa-3 op-info-box d-flex align-center justify-space-between">
            <div>
              <div class="caption text-muted">المركبة الحالية:</div>
              <div class="font-weight-bold text-navy">{{ driverForVehicle.bus.license }} (سعة {{ driverForVehicle.bus.capacity }})</div>
            </div>
            <v-btn
              small
              depressed
              color="#FEE2E2"
              class="text-rose font-weight-bold"
              :loading="isSubmittingVehicle"
              @click="unassignCurrentVehicle"
            >
              إلغاء التعيين
            </v-btn>
          </div>

          <!-- Available Vehicles Selector -->
          <div class="mb-2">
            <v-select
              v-model="selectedBusId"
              :items="availableBuses"
              item-text="license_label"
              item-value="id"
              label="اختر المركبة المتاحة *"
              outlined
              dense
              :loading="loadingBuses"
              hide-details="auto"
              class="op-filter-select"
            >
              <template v-slot:item="{ item }">
                <div class="d-flex align-center justify-space-between w-100 py-1">
                  <div>
                    <span class="font-weight-bold text-navy me-2">{{ item.license }}</span>
                    <span class="caption text-muted">({{ item.type }} - سعة {{ item.capacity }} راكب)</span>
                  </div>
                  <v-chip v-if="item.is_current" x-small color="#EFF6FF" text-color="#1E40AF">المركبة الحالية</v-chip>
                </div>
              </template>
            </v-select>
          </div>

          <div v-if="availableBuses.length === 0 && !loadingBuses" class="text-rose caption mt-2">
            لا توجد حافلات متاحة للتعيين حالياً.
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text color="#64748B" @click="assignModalOpen = false" :disabled="isSubmittingVehicle">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="#082C69"
            class="white--text font-weight-bold"
            :loading="isSubmittingVehicle"
            :disabled="!selectedBusId || isSubmittingVehicle"
            @click="submitAssignVehicle"
          >
            تأكيد التعيين
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== MODAL 3: OPERATIONAL HISTORY (SCOPED) ==================== -->
    <v-dialog v-model="historyModalOpen" max-width="1100" persistent>
      <v-card v-if="driverForHistory" class="op-dialog-card">
        <v-card-title class="d-flex align-center justify-space-between pb-3 op-dialog-header">
          <div class="d-flex align-center">
            <v-icon color="#082C69" class="me-2">mdi-history</v-icon>
            <div>
              <div class="font-weight-bold text-navy" style="font-size: 17px;">سجل التشغيل — {{ driverForHistory.name }}</div>
              <div class="caption text-muted">الرحلات المنفذة ضمن نطاق مجموعات التشغيل المسندة إليك فقط</div>
            </div>
          </div>
          <v-btn icon small @click="historyModalOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-data-table
            :headers="historyHeaders"
            :items="historyTrips"
            :loading="loadingHistory"
            :server-items-length="totalHistoryTrips"
            :page.sync="historyPage"
            :items-per-page.sync="historyPerPage"
            :footer-props="{
              'items-per-page-options': [10, 25, 50],
              'items-per-page-text': 'الصفوف:'
            }"
            class="op-data-table"
            @update:page="fetchDriverHistory"
            @update:items-per-page="fetchDriverHistory"
          >
            <!-- Planned Reference -->
            <template v-slot:item.planned_trip_reference="{ item }">
              <span class="font-weight-bold text-navy">{{ item.planned_trip_reference }}</span>
            </template>

            <!-- Parent Reference -->
            <template v-slot:item.parent_trip_reference="{ item }">
              <v-chip x-small color="#F1F5F9" class="font-weight-bold">{{ item.parent_trip_reference }}</v-chip>
            </template>

            <!-- Status -->
            <template v-slot:item.status_label="{ item }">
              <div class="op-status-badge d-inline-flex" :style="{ backgroundColor: getBadgeBgColor(item.status_color), color: getBadgeTextColor(item.status_color), borderColor: getBadgeBorderColor(item.status_color) }">
                {{ item.status_label }}
              </div>
            </template>

            <!-- Delay -->
            <template v-slot:item.delay_minutes="{ item }">
              <span v-if="item.delay_minutes > 0" class="text-rose font-weight-bold">
                {{ item.delay_minutes }} دقيقة
              </span>
              <span v-else class="text-emerald font-weight-medium">
                في الموعد
              </span>
            </template>

            <!-- Empty State -->
            <template v-slot:no-data>
              <div class="py-8 text-center text-muted">
                <v-icon size="42" color="#94A3B8" class="mb-2">mdi-bus-stop</v-icon>
                <div class="font-weight-bold text-navy mb-1">لا توجد رحلات مسجلة</div>
                <div class="caption">لم يقم السائق بتشغيل رحلات تابعة لمجموعاتك المعينة حتى الآن.</div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text color="#64748B" @click="historyModalOpen = false">إغلاق</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== MODAL 4: SUSPEND DRIVER ==================== -->
    <v-dialog v-model="suspendModalOpen" max-width="480" persistent>
      <v-card v-if="driverToSuspend" class="op-dialog-card">
        <v-card-title class="d-flex align-center pb-2">
          <v-icon color="#EF4444" class="me-2">mdi-alert-circle-outline</v-icon>
          <span class="font-weight-bold text-rose" style="font-size: 17px;">إيقاف السائق مؤقتاً</span>
        </v-card-title>

        <v-card-text class="pt-3">
          <p class="body-1 text-navy mb-3">
            هل أنت متأكد من رغبتك في إيقاف السائق <strong>{{ driverToSuspend.name }}</strong>؟
          </p>
          <v-alert dense outlined color="#EF4444" class="caption mb-0">
            لن يكون السائق متاحاً للتعيين على أي رحلات أو تشغيلات جديدة حتى تتم إعادة تفعيله.
          </v-alert>

          <!-- Error Alert if running trip safety blocks suspension -->
          <v-alert v-if="suspendError" type="error" dense class="mt-3 caption" outlined>
            {{ suspendError }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text color="#64748B" @click="suspendModalOpen = false" :disabled="isSubmittingAction">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="#EF4444"
            class="white--text font-weight-bold"
            :loading="isSubmittingAction"
            @click="submitSuspendDriver"
          >
            تأكيد الإيقاف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== MODAL 5: REACTIVATE DRIVER ==================== -->
    <v-dialog v-model="reactivateModalOpen" max-width="480" persistent>
      <v-card v-if="driverToReactivate" class="op-dialog-card">
        <v-card-title class="d-flex align-center pb-2">
          <v-icon color="#10B981" class="me-2">mdi-check-circle-outline</v-icon>
          <span class="font-weight-bold text-emerald" style="font-size: 17px;">إعادة تفعيل السائق</span>
        </v-card-title>

        <v-card-text class="pt-3">
          <p class="body-1 text-navy mb-3">
            هل أنت متأكد من إعادة تفعيل السائق <strong>{{ driverToReactivate.name }}</strong>؟
          </p>
          <div class="caption text-muted">
            سيعود السائق إلى الحالة النشطة وسيكون متاحاً للتعيين التشغيلي.
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text color="#64748B" @click="reactivateModalOpen = false" :disabled="isSubmittingAction">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="#10B981"
            class="white--text font-weight-bold"
            :loading="isSubmittingAction"
            @click="submitReactivateDriver"
          >
            تأكيد إعادة التفعيل
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Driver Modal -->
    <v-dialog v-model="addDriverModalOpen" max-width="600" persistent>
      <v-card class="op-dialog-card rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pb-3 op-dialog-header">
          <div class="d-flex align-center">
            <v-icon color="#082C69" class="me-2">mdi-account-plus</v-icon>
            <span class="font-weight-bold" style="color: #082C69;">إضافة سائق جديد</span>
          </div>
          <v-btn icon small @click="closeAddDriverDialog" :disabled="isSubmittingAddDriver">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-5 px-6">
          <v-alert v-if="addDriverError" dense outlined type="error" class="mb-4">
            {{ addDriverError }}
          </v-alert>

          <v-row dense>
            <v-col cols="12" sm="6">
              <label class="caption font-weight-bold mb-1 d-block text-navy">الاسم الكامل *</label>
              <v-text-field
                v-model="addDriverForm.name"
                placeholder="أدخل اسم السائق"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption font-weight-bold mb-1 d-block text-navy">رقم الهاتف *</label>
              <v-text-field
                v-model="addDriverForm.phone"
                placeholder="مثال: 01012345678"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <label class="caption font-weight-bold mb-1 d-block text-navy">البريد الإلكتروني (اختياري)</label>
              <v-text-field
                v-model="addDriverForm.email"
                placeholder="driver@example.com"
                type="email"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption font-weight-bold mb-1 d-block text-navy">كلمة المرور *</label>
              <v-text-field
                v-model="addDriverForm.password"
                placeholder="6 أحرف على الأقل"
                type="password"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption font-weight-bold mb-1 d-block text-navy">تأكيد كلمة المرور *</label>
              <v-text-field
                v-model="addDriverForm.password_confirmation"
                placeholder="أعد إدخال كلمة المرور"
                type="password"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption font-weight-bold mb-1 d-block text-navy">نوع السائق *</label>
              <v-select
                v-model="addDriverForm.driver_type"
                :items="[
                  { text: 'بدون مركبة', value: 'without_vehicle' },
                  { text: 'مع مركبة', value: 'with_vehicle' }
                ]"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption font-weight-bold mb-1 d-block text-navy">حالة العمل *</label>
              <v-select
                v-model="addDriverForm.working_status"
                :items="[
                  { text: 'نشط', value: 'active' },
                  { text: 'غير نشط', value: 'inactive' }
                ]"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
              ></v-select>
            </v-col>

            <template v-if="addDriverForm.driver_type === 'with_vehicle'">
              <v-col cols="12" sm="6">
                <label class="caption font-weight-bold mb-1 d-block text-navy">نوع المركبة *</label>
                <v-select
                  v-model="addDriverForm.vehicle_type"
                  :items="[
                    { text: 'هاي اس (Hiace)', value: 'hiace' },
                    { text: 'ميني باص (Mini Bus)', value: 'mini_bus' },
                    { text: 'كوستر (Coaster)', value: 'coaster' },
                    { text: 'باص كبير (Bus)', value: 'bus' },
                    { text: 'سيارة ملاكي (Car)', value: 'car' },
                  ]"
                  outlined
                  dense
                  hide-details="auto"
                  class="mb-3"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="caption font-weight-bold mb-1 d-block text-navy">لوحة المركبة *</label>
                <v-text-field
                  v-model="addDriverForm.vehicle_license_number"
                  placeholder="مثال: أ ب ج 1234"
                  outlined
                  dense
                  hide-details="auto"
                  class="mb-3"
                ></v-text-field>
              </v-col>
            </template>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-6 py-4 justify-end gap-2">
          <v-btn text :disabled="isSubmittingAddDriver" @click="closeAddDriverDialog">
            إلغاء
          </v-btn>
          <v-btn
            depressed
            color="#082C69"
            class="white--text font-weight-bold"
            :loading="isSubmittingAddDriver"
            @click="submitAddDriver"
          >
            إضافة السائق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import VueElementLoading from 'vue-element-loading';

export default {
  name: 'OperationsDrivers',
  components: {
    VueElementLoading,
  },
  data() {
    return {
      isLoading: false,
      tableLoading: false,
      searchQuery: '',
      searchDebounceTimer: null,
      operationalStatusFilter: 'all',
      vehicleStatusFilter: 'all',
      tripGroupFilter: 'all',
      page: 1,
      perPage: 10,
      totalDrivers: 0,
      driversList: [],
      assignedGroups: [],
      activeMenuRowId: null,
      kpis: {
        total_drivers: 0,
        active_drivers: 0,
        with_vehicle: 0,
        without_vehicle: 0,
        inactive_suspended: 0,
      },
      headers: [
        { text: 'الاسم', value: 'name', sortable: false, width: '22%' },
        { text: 'رقم الهاتف', value: 'phone', sortable: false, width: '18%' },
        { text: 'المركبة المرتبطة', value: 'bus', sortable: false, width: '22%' },
        { text: 'الحالة', value: 'badge', sortable: false, width: '16%' },
        { text: 'التقييم', value: 'rating', sortable: false, width: '12%' },
        { text: 'الإجراءات', value: 'actions', sortable: false, align: 'center', width: '10%' },
      ],
      operationalStatusOptions: [
        { text: 'الكل', value: 'all' },
        { text: 'نشط', value: 'active' },
        { text: 'موقوف', value: 'suspended' },
        { text: 'قيد المراجعة', value: 'under_review' },
      ],
      vehicleStatusOptions: [
        { text: 'الكل', value: 'all' },
        { text: 'مرتبط بمركبة', value: 'with_vehicle' },
        { text: 'بدون مركبة', value: 'without_vehicle' },
      ],

      // Driver Details Modal
      detailsModalOpen: false,
      detailsTab: 0,
      selectedDriverDetails: null,
      docPreviewModalOpen: false,
      activePreviewDoc: null,

      // Assign / Change Vehicle Modal
      assignModalOpen: false,
      driverForVehicle: null,
      selectedBusId: null,
      availableBuses: [],
      loadingBuses: false,
      isSubmittingVehicle: false,

      // Operational History Modal
      historyModalOpen: false,
      driverForHistory: null,
      historyTrips: [],
      totalHistoryTrips: 0,
      historyPage: 1,
      historyPerPage: 10,
      loadingHistory: false,
      historyHeaders: [
        { text: 'التاريخ', value: 'planned_date', sortable: false },
        { text: 'رقم الرحلة', value: 'planned_trip_reference', sortable: false },
        { text: 'الرحلة الرئيسية', value: 'parent_trip_reference', sortable: false },
        { text: 'المسار', value: 'route_name', sortable: false },
        { text: 'المجدول', value: 'scheduled_start_time', sortable: false },
        { text: 'البداية الفعلية', value: 'actual_start_time', sortable: false },
        { text: 'النهاية', value: 'actual_end_time', sortable: false },
        { text: 'المركبة', value: 'bus_license', sortable: false },
        { text: 'الحالة', value: 'status_label', sortable: false },
        { text: 'التأخير', value: 'delay_minutes', sortable: false },
        { text: 'الحجوزات', value: 'reservations_count', sortable: false },
      ],

      // Suspend / Reactivate Modals
      suspendModalOpen: false,
      driverToSuspend: null,
      suspendError: null,
      reactivateModalOpen: false,
      driverToReactivate: null,
      isSubmittingAction: false,

      // Add Driver Modal
      addDriverModalOpen: false,
      isSubmittingAddDriver: false,
      addDriverForm: {
        name: '',
        phone: '',
        email: '',
        password: '',
        password_confirmation: '',
        driver_type: 'without_vehicle',
        working_status: 'active',
        vehicle_type: 'hiace',
        vehicle_license_number: '',
      },
      addDriverError: '',
    };
  },
  computed: {
    tripGroupOptions() {
      const options = [{ text: 'الكل', value: 'all' }];
      this.assignedGroups.forEach((g) => {
        options.push({ text: g.label, value: g.id });
      });
      return options;
    },
    totalPages() {
      return Math.ceil(this.totalDrivers / this.perPage) || 1;
    },
    paginationText() {
      if (this.totalDrivers === 0) return '0 من 0 سائق';
      const start = (this.page - 1) * this.perPage + 1;
      const end = Math.min(this.page * this.perPage, this.totalDrivers);
      return `${start}-${end} من ${this.totalDrivers} سائق`;
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.page;
      if (total <= 7) {
        const pages = [];
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }
      if (current <= 4) {
        return [1, 2, 3, 4, 5, '...', total];
      }
      if (current >= total - 3) {
        return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
      }
      return [1, '...', current - 1, current, current + 1, '...', total];
    },
  },
  mounted() {
    this.fetchDrivers();
  },
  methods: {
    async fetchDrivers() {
      this.tableLoading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
          operational_status: this.operationalStatusFilter,
          vehicle_status: this.vehicleStatusFilter,
          trip_group_id: this.tripGroupFilter,
        };
        if (this.searchQuery && this.searchQuery.trim() !== '') {
          params.search = this.searchQuery.trim();
        }

        const response = await axios.get('/operations/drivers', { params });
        if (response.data) {
          const data = response.data;
          this.driversList = (data.drivers && data.drivers.data) || [];
          this.totalDrivers = (data.drivers && data.drivers.total) || 0;
          if (data.kpis) {
            this.kpis = data.kpis;
          }
          if (data.assigned_groups) {
            this.assignedGroups = data.assigned_groups;
          }
        }
      } catch (err) {
        console.error('Failed to load drivers:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل بيانات السائقين.',
          type: 'error',
        });
      } finally {
        this.tableLoading = false;
        this.isLoading = false;
      }
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1;
        this.fetchDrivers();
      }, 400);
    },
    onSearchEnter() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.page = 1;
      this.fetchDrivers();
    },
    onSearchClear() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchQuery = '';
      this.page = 1;
      this.$nextTick(() => {
        this.fetchDrivers();
      });
    },
    onPageChange(newPage) {
      if (newPage < 1 || newPage > this.totalPages || newPage === this.page) return;
      this.page = newPage;
      this.fetchDrivers();
    },
    onPerPageChange(newPerPage) {
      this.perPage = Number(newPerPage);
      this.page = 1;
      this.fetchDrivers();
    },
    resetFilters() {
      this.searchQuery = '';
      this.operationalStatusFilter = 'all';
      this.vehicleStatusFilter = 'all';
      this.tripGroupFilter = 'all';
      this.page = 1;
      this.fetchDrivers();
    },
    getTableRowClass(item) {
      return this.activeMenuRowId === item.id ? 'op-table-row row-active' : 'op-table-row';
    },
    onMenuToggle(id, isOpen) {
      this.activeMenuRowId = isOpen ? id : null;
    },
    getInitials(name) {
      if (!name) return 'س';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + ' ' + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2);
    },
    getAvatarColor(name) {
      const palettes = [
        { bg: '#EEF2FF', text: '#3730A3' },
        { bg: '#F0FDF4', text: '#166534' },
        { bg: '#FEF3C7', text: '#92400E' },
        { bg: '#E0F2FE', text: '#075985' },
        { bg: '#FCE7F3', text: '#9D174D' },
        { bg: '#F3E8FF', text: '#6B21A8' },
        { bg: '#E2E8F0', text: '#1E293B' },
      ];
      if (!name) return palettes[0];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % palettes.length;
      return palettes[index];
    },
    getBadgeStyle(item) {
      const statusKey = item && item.badge ? item.badge.status_key : '';
      switch (statusKey) {
        case 'active_with_bus':
          return {
            backgroundColor: '#DCFCE7',
            color: '#15803D',
            borderColor: '#BBF7D0',
          };
        case 'available_without_bus':
          return {
            backgroundColor: '#FFF3D6',
            color: '#C56A00',
            borderColor: '#FED7AA',
          };
        case 'on_trip':
          return {
            backgroundColor: '#E0F2FE',
            color: '#0369A1',
            borderColor: '#BAE6FD',
          };
        case 'suspended':
          return {
            backgroundColor: '#FEE2E2',
            color: '#DC2626',
            borderColor: '#FECACA',
          };
        case 'under_review':
          return {
            backgroundColor: '#FEF3C7',
            color: '#B45309',
            borderColor: '#FDE68A',
          };
        default:
          return {
            backgroundColor: '#EEF1F5',
            color: '#596579',
            borderColor: '#CBD5E1',
          };
      }
    },
    getBadgeBgColor(colorKey) {
      switch (colorKey) {
        case 'success':
        case '#10B981':
          return '#DCFCE7';
        case 'error':
        case '#EF4444':
          return '#FEE2E2';
        case 'warning':
        case '#F59E0B':
          return '#FFF3D6';
        case 'info':
        case '#0284C7':
        case '#3B82F6':
          return '#E0F2FE';
        default:
          return '#EEF1F5';
      }
    },
    getBadgeTextColor(colorKey) {
      switch (colorKey) {
        case 'success':
        case '#10B981':
          return '#15803D';
        case 'error':
        case '#EF4444':
          return '#DC2626';
        case 'warning':
        case '#F59E0B':
          return '#C56A00';
        case 'info':
        case '#0284C7':
        case '#3B82F6':
          return '#0369A1';
        default:
          return '#596579';
      }
    },
    getBadgeBorderColor(colorKey) {
      switch (colorKey) {
        case 'success':
        case '#10B981':
          return '#BBF7D0';
        case 'error':
        case '#EF4444':
          return '#FECACA';
        case 'warning':
        case '#F59E0B':
          return '#FED7AA';
        case 'info':
        case '#0284C7':
        case '#3B82F6':
          return '#BAE6FD';
        default:
          return '#CBD5E1';
      }
    },
    getBadgeIcon(statusKey) {
      switch (statusKey) {
        case 'active_with_bus':
          return 'mdi-check-circle';
        case 'available_without_bus':
          return 'mdi-car-off';
        case 'on_trip':
          return 'mdi-steering';
        case 'suspended':
          return 'mdi-pause-circle-outline';
        case 'under_review':
          return 'mdi-clock-outline';
        default:
          return 'mdi-circle-small';
      }
    },

    // Modal 1: Details
    async openDetailsModal(driver) {
      this.isLoading = true;
      try {
        const res = await axios.get(`/operations/drivers/${driver.id}`);
        this.selectedDriverDetails = res.data;
        this.detailsTab = 0;
        this.detailsModalOpen = true;
      } catch (err) {
        console.error('Failed to get driver details:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل تفاصيل السائق.',
          type: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
    openAssignFromDetails() {
      if (!this.selectedDriverDetails) return;
      this.openAssignVehicleModal(this.selectedDriverDetails);
    },
    async unassignFromDetails() {
      if (!this.selectedDriverDetails) return;
      this.driverForVehicle = this.selectedDriverDetails;
      await this.unassignCurrentVehicle();
    },
    previewDocument(doc) {
      this.activePreviewDoc = doc;
      this.docPreviewModalOpen = true;
    },
    downloadDocument(doc) {
      if (!doc || !doc.file_url) return;
      window.open(doc.file_url, '_blank');
    },
    getDocIcon(docName) {
      if (!docName) return 'mdi-file-document-outline';
      if (docName.includes('قومي') || docName.includes('هوية')) return 'mdi-card-account-details-outline';
      if (docName.includes('قيادة') || docName.includes('رخصة قيادة')) return 'mdi-card-account-details-star-outline';
      if (docName.includes('مركبة') || docName.includes('تسيير') || docName.includes('سيارة')) return 'mdi-car-info';
      if (docName.includes('فيش') || docName.includes('جنائي')) return 'mdi-shield-account-outline';
      if (docName.includes('تحليل') || docName.includes('مخدرات') || docName.includes('طبي')) return 'mdi-medical-bag';
      return 'mdi-file-document-outline';
    },
    getDocBadgeBg(statusKey) {
      switch (statusKey) {
        case 'active':
          return '#DCFCE7';
        case 'near_expiry':
          return '#FFF3D6';
        case 'expired':
        case 'needs_update':
          return '#FEE2E2';
        default:
          return '#EEF1F5';
      }
    },
    getDocBadgeColor(statusKey) {
      switch (statusKey) {
        case 'active':
          return '#15803D';
        case 'near_expiry':
          return '#C56A00';
        case 'expired':
        case 'needs_update':
          return '#DC2626';
        default:
          return '#63738A';
      }
    },

    // Modal 2: Assign / Change Vehicle
    async openAssignVehicleModal(driver) {
      this.driverForVehicle = driver;
      this.selectedBusId = driver.bus ? driver.bus.id : null;
      this.assignModalOpen = true;
      this.loadingBuses = true;
      try {
        const res = await axios.get('/operations/drivers/available-vehicles', {
          params: { driver_id: driver.id },
        });
        this.availableBuses = (res.data || []).map((b) => ({
          ...b,
          license_label: `${b.license} (${b.type} - سعة ${b.capacity})`,
        }));
      } catch (err) {
        console.error('Failed to load available buses:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل قائمة المركبات المتاحة.',
          type: 'error',
        });
      } finally {
        this.loadingBuses = false;
      }
    },
    async submitAssignVehicle() {
      if (!this.selectedBusId || !this.driverForVehicle) return;
      this.isSubmittingVehicle = true;
      try {
        const res = await axios.post(`/operations/drivers/${this.driverForVehicle.id}/assign-vehicle`, {
          bus_id: this.selectedBusId,
        });
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تم تعيين المركبة بنجاح.',
          type: 'success',
        });
        this.assignModalOpen = false;
        this.fetchDrivers();
        if (this.detailsModalOpen && this.selectedDriverDetails) {
          const updatedId = this.driverForVehicle.id;
          const freshRes = await axios.get(`/operations/drivers/${updatedId}`);
          this.selectedDriverDetails = freshRes.data;
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر تعيين المركبة.';
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.isSubmittingVehicle = false;
      }
    },
    async unassignCurrentVehicle() {
      if (!this.driverForVehicle) return;
      this.isSubmittingVehicle = true;
      try {
        const res = await axios.post(`/operations/drivers/${this.driverForVehicle.id}/unassign-vehicle`);
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تم إلغاء ارتباط المركبة بنجاح.',
          type: 'success',
        });
        this.assignModalOpen = false;
        this.fetchDrivers();
        if (this.detailsModalOpen && this.selectedDriverDetails) {
          const updatedId = this.driverForVehicle.id;
          const freshRes = await axios.get(`/operations/drivers/${updatedId}`);
          this.selectedDriverDetails = freshRes.data;
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر إلغاء ارتباط المركبة.';
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.isSubmittingVehicle = false;
      }
    },

    // Modal 3: History
    async openHistoryModal(driver) {
      this.driverForHistory = driver;
      this.historyTrips = [];
      this.historyPage = 1;
      this.historyModalOpen = true;
      await this.fetchDriverHistory();
    },
    async fetchDriverHistory() {
      if (!this.driverForHistory) return;
      this.loadingHistory = true;
      try {
        const res = await axios.get(`/operations/drivers/${this.driverForHistory.id}/history`, {
          params: {
            page: this.historyPage,
            per_page: this.historyPerPage,
            trip_group_id: this.tripGroupFilter !== 'all' ? this.tripGroupFilter : null,
          },
        });
        if (res.data) {
          this.historyTrips = res.data.data || [];
          this.totalHistoryTrips = res.data.total || 0;
        }
      } catch (err) {
        console.error('Failed to load history:', err);
        this.$notify({
          title: 'خطأ',
          text: 'تعذر تحميل سجل التشغيل.',
          type: 'error',
        });
      } finally {
        this.loadingHistory = false;
      }
    },

    // Modal 4: Suspend
    openSuspendModal(driver) {
      this.driverToSuspend = driver;
      this.suspendError = null;
      this.suspendModalOpen = true;
    },
    async submitSuspendDriver() {
      if (!this.driverToSuspend) return;
      this.isSubmittingAction = true;
      this.suspendError = null;
      try {
        const res = await axios.post(`/operations/drivers/${this.driverToSuspend.id}/suspend`);
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تم إيقاف السائق بنجاح.',
          type: 'success',
        });
        this.suspendModalOpen = false;
        this.fetchDrivers();
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر إيقاف السائق.';
        this.suspendError = msg;
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.isSubmittingAction = false;
      }
    },

    // Modal 5: Reactivate
    openReactivateModal(driver) {
      this.driverToReactivate = driver;
      this.reactivateModalOpen = true;
    },
    async submitReactivateDriver() {
      if (!this.driverToReactivate) return;
      this.isSubmittingAction = true;
      try {
        const res = await axios.post(`/operations/drivers/${this.driverToReactivate.id}/reactivate`);
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تم إعادة تفعيل السائق بنجاح.',
          type: 'success',
        });
        this.reactivateModalOpen = false;
        this.fetchDrivers();
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'تعذر إعادة تفعيل السائق.';
        this.$notify({
          title: 'خطأ',
          text: msg,
          type: 'error',
        });
      } finally {
        this.isSubmittingAction = false;
      }
    },
    // Add Driver Flow
    openAddDriverDialog() {
      this.addDriverForm = {
        name: '',
        phone: '',
        email: '',
        password: '',
        password_confirmation: '',
        driver_type: 'without_vehicle',
        working_status: 'active',
        vehicle_type: 'hiace',
        vehicle_license_number: '',
      };
      this.addDriverError = '';
      this.addDriverModalOpen = true;
    },
    closeAddDriverDialog() {
      this.addDriverModalOpen = false;
      this.addDriverError = '';
    },
    async submitAddDriver() {
      if (!this.addDriverForm.name.trim() || !this.addDriverForm.phone.trim() || !this.addDriverForm.password) {
        this.addDriverError = 'يرجى ملء جميع الحقول المطلوبة (الاسم، الهاتف، كلمة المرور).';
        return;
      }
      if (this.addDriverForm.password !== this.addDriverForm.password_confirmation) {
        this.addDriverError = 'كلمة المرور وتأكيد كلمة المرور غير متطابقين.';
        return;
      }
      if (this.addDriverForm.driver_type === 'with_vehicle' && !this.addDriverForm.vehicle_license_number.trim()) {
        this.addDriverError = 'يرجى إدخال رقم لوحة المركبة.';
        return;
      }

      this.isSubmittingAddDriver = true;
      this.addDriverError = '';

      try {
        const res = await axios.post('/users/drivers', this.addDriverForm);
        this.$notify({
          title: 'نجاح',
          text: res.data.message || 'تمت إضافة السائق بنجاح.',
          type: 'success',
        });
        this.closeAddDriverDialog();
        this.fetchDrivers();
      } catch (err) {
        console.error('Error adding driver:', err);
        const msg = (err.response && err.response.data && err.response.data.message)
          || 'تعذر إضافة السائق. يرجى التحقق من البيانات والمحاولة مرة أخرى.';
        this.addDriverError = msg;
      } finally {
        this.isSubmittingAddDriver = false;
      }
    },
  },
};
</script>

<style scoped>
.operations-container {
  width: 100%;
  max-width: 1650px;
  margin: 0 auto;
  padding: 24px 32px;
  background-color: #F5F7FA;
  min-height: 100vh;
  font-family: 'Cairo', sans-serif;
  color: #142B4A;
}

/* Header */
.op-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.op-main-title {
  font-size: 26px;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.2;
}

.op-scope-pill {
  padding: 4px 12px;
  background: #E0F2FE;
  border: 1px solid #BAE6FD;
  border-radius: 20px;
  color: #0369A1;
  font-size: 12.5px;
}

.op-subtitle {
  font-size: 14px;
  color: #63738A;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.op-btn-add-driver {
  background-color: #082C69 !important;
  color: #FFFFFF !important;
  border: 1px solid #082C69 !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 18px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  opacity: 0.85 !important;
  cursor: not-allowed !important;
  box-shadow: 0 2px 4px rgba(8, 44, 105, 0.15) !important;
}

.op-btn-refresh {
  background-color: #FFFFFF !important;
  border: 1px solid #E1E7EF !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 16px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #082C69 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.op-btn-refresh:hover:not(:disabled) {
  background-color: #F8FAFC !important;
  border-color: #082C69 !important;
}

/* 5 KPI Cards Grid - Guaranteed 5 columns on desktop */
.op-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

@media (max-width: 1264px) {
  .op-kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .op-kpi-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}

.op-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 98px;
}

.op-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(8, 44, 105, 0.06);
}

.kpi-label {
  font-size: 13.5px;
  font-weight: 600;
  color: #63738A;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
}

.kpi-icon-side {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Filter Bar */
.op-filter-bar {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.filter-inputs-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
}

.filter-search-box {
  flex: 1.5;
  min-width: 260px;
}

.filter-select-box {
  min-width: 170px;
  flex: 1;
}

.filter-actions-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.op-filter-btn {
  height: 40px;
  padding: 0 14px;
  border: 1px solid #DFE5EE;
  background: #FFFFFF;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #63738A;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-filter-btn:hover {
  background: #F8FAFC;
  border-color: #CBD5E1;
  color: #142B4A;
}

.op-adv-filter-btn {
  color: #082C69;
  border-color: #D4DEF0;
  background: #F8FAFF;
}

.op-adv-filter-btn:hover {
  background: #EEF2FF;
  border-color: #082C69;
}

/* Vuetify Field Overrides */
.op-search-input >>> .v-input__slot,
.op-filter-select >>> .v-input__slot {
  min-height: 42px !important;
  background-color: #FFFFFF !important;
  border-radius: 8px !important;
}

.op-search-input >>> fieldset,
.op-filter-select >>> fieldset {
  border-color: #DFE5EE !important;
  border-width: 1px !important;
}

.op-search-input:hover >>> fieldset,
.op-filter-select:hover >>> fieldset {
  border-color: #082C69 !important;
}

.op-search-input >>> input {
  font-size: 13.5px !important;
  color: #142B4A !important;
  font-weight: 500 !important;
}

.op-search-input >>> input::placeholder {
  color: #63738A !important;
  opacity: 1 !important;
}

.op-filter-select >>> .v-select__selections {
  font-size: 13.5px !important;
  color: #142B4A !important;
  font-weight: 600 !important;
}

.op-filter-select >>> .v-label {
  font-size: 13px !important;
  color: #63738A !important;
}

/* Table Card */
.op-table-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.op-data-table >>> th {
  background-color: #F8FAFC !important;
  color: #23395D !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  border-bottom: 1px solid #E1E7EF !important;
  height: 50px !important;
  padding: 0 20px !important;
}

.op-data-table >>> td {
  border-bottom: 1px solid #EDF0F4 !important;
  font-size: 13.5px !important;
  padding: 10px 20px !important;
  height: 58px !important;
  vertical-align: middle !important;
}

.op-table-row {
  transition: background-color 0.15s ease;
}

.op-table-row:hover {
  background-color: #F8FAFC !important;
}

.op-table-row.row-active {
  background-color: #F8FAFC !important;
  box-shadow: inset 0 0 0 1px #CBD5E1;
}

/* Driver Cell */
.driver-avatar-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  overflow: hidden;
}

.driver-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.driver-name-text {
  font-weight: 700;
  color: #142B4A;
  font-size: 14.5px;
  line-height: 1.2;
}

.driver-id-caption {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 2px;
  font-weight: 500;
}

/* Phone Cell */
.phone-cell-content {
  font-size: 13.5px;
  font-weight: 600;
  color: #52637A;
}

/* Vehicle Cell */
.vehicle-cell-content {
  font-size: 13.5px;
}

.vehicle-plate-text {
  font-weight: 700;
  color: #142B4A;
}

.vehicle-capacity-text {
  font-size: 12px;
  color: #63738A;
  font-weight: 500;
}

.vehicle-unlinked-text {
  font-size: 13px;
  font-weight: 600;
  color: #94A3B8;
  display: inline-flex;
  align-items: center;
}

/* Status Badges */
.op-status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 700;
  border: 1px solid;
  line-height: 1;
}

/* Rating Cell */
.rating-cell-content {
  font-size: 14px;
}

.rating-avg-text {
  font-weight: 700;
  color: #142B4A;
}

.rating-count-text {
  font-size: 12px;
  color: #63738A;
  font-weight: 500;
}

.rating-empty-text {
  font-size: 14px;
  color: #94A3B8;
  font-weight: 600;
}

/* Action Button */
.op-action-btn {
  width: 40px;
  height: 32px;
  background: #FFFFFF;
  border: 1px solid #DDE3EC;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.op-action-btn:hover,
.op-action-btn.btn-active {
  border-color: #082C69;
  background: #F8FAFC;
}

.op-action-btn:hover i,
.op-action-btn.btn-active i {
  color: #082C69 !important;
}

.op-actions-menu {
  border-radius: 10px !important;
  min-width: 200px;
  box-shadow: 0 10px 25px -5px rgba(8, 44, 105, 0.15), 0 8px 10px -6px rgba(8, 44, 105, 0.1) !important;
}

/* Custom Pagination */
.op-pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid #E1E7EF;
  background: #FFFFFF;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-label {
  font-size: 13.5px;
  color: #63738A;
  font-weight: 600;
}

.op-rows-select {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #DFE5EE;
  border-radius: 6px;
  background-color: #FFFFFF;
  font-size: 13.5px;
  font-weight: 600;
  color: #142B4A;
  cursor: pointer;
  outline: none;
}

.op-rows-select:focus {
  border-color: #082C69;
}

.pagination-info {
  font-size: 13.5px;
  font-weight: 600;
  color: #63738A;
}

.op-page-nav-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #DFE5EE;
  border-radius: 6px;
  background: #FFFFFF;
  color: #63738A;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.op-page-nav-btn:hover:not(:disabled) {
  border-color: #082C69;
  color: #082C69;
  background: #F8FAFC;
}

.op-page-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.op-page-number-btn {
  min-width: 34px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #DFE5EE;
  border-radius: 6px;
  background: #FFFFFF;
  color: #63738A;
  font-size: 13.5px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.op-page-number-btn:hover {
  background: #F8FAFC;
  border-color: #CBD5E1;
  color: #142B4A;
}

.op-page-number-btn.active {
  background: #082C69;
  border-color: #082C69;
  color: #FFFFFF;
  font-weight: 700;
}

.op-page-ellipsis {
  padding: 0 4px;
  color: #94A3B8;
  font-weight: 700;
}

/* Modals */
.op-dialog-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.op-dialog-header {
  border-bottom: 1px solid #E1E7EF;
  background: #F8FAFC;
}

.detail-label {
  font-size: 12.5px;
  color: #63738A;
  margin-bottom: 2px;
  font-weight: 500;
}

.detail-val {
  font-size: 14.5px;
  font-weight: 700;
  color: #142B4A;
  margin-bottom: 12px;
}

.op-info-box {
  background: #F8FAFC;
  border: 1px solid #E1E7EF;
  border-radius: 10px;
}

.op-stat-box {
  background: #F8FAFC;
  border: 1px solid #E1E7EF;
  border-radius: 10px;
  padding: 14px;
  text-align: center;
}

.stat-number {
  font-size: 26px;
  font-weight: 800;
}

.stat-title {
  font-size: 12.5px;
  color: #63738A;
  margin-top: 4px;
  font-weight: 600;
}

/* Semantic Colors */
.text-navy {
  color: #082C69 !important;
}

.text-emerald {
  color: #15803D !important;
}

.text-blue {
  color: #0284C7 !important;
}

.text-amber {
  color: #D97706 !important;
}

.text-rose {
  color: #DC2626 !important;
}

.text-muted {
  color: #63738A !important;
}

/* ==================== DRIVER DETAILS MODAL STYLES ==================== */
::v-deep .op-details-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 40px -10px rgba(8, 44, 105, 0.25) !important;
}

.op-modal-tabs-wrapper {
  border-bottom: 1px solid #E1E7EF;
  background: #FFFFFF;
}

::v-deep .op-modal-tabs .v-tab {
  font-family: 'Cairo', sans-serif !important;
  font-weight: 700 !important;
  font-size: 14.5px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  color: #63738A !important;
  padding: 0 20px !important;
  min-height: 48px !important;
}

::v-deep .op-modal-tabs .v-tab--active {
  color: #082C69 !important;
}

.op-modal-scroll-body {
  background-color: #F8FAFC;
  max-height: calc(90vh - 130px);
}

/* Tab 1: Basic Data */
.profile-hero-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 14px;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.profile-hero-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  overflow: hidden;
  flex-shrink: 0;
}

.profile-hero-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-hero-name {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
}

.profile-hero-id {
  font-size: 13px;
  font-weight: 600;
  color: #63738A;
}

.profile-meta-specs {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-spec-item {
  background: #F8FAFC;
  border: 1px solid #E1E7EF;
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  font-size: 13px;
}

.spec-label {
  color: #63738A;
  margin-inline-end: 6px;
  font-weight: 500;
}

.op-info-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.op-card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #F1F5F9;
}

.op-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #082C69;
}

.op-card-content {
  display: flex;
  flex-direction: column;
}

.info-grid-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px dashed #E2E8F0;
  font-size: 13.5px;
}

.info-grid-label {
  color: #63738A;
  font-weight: 500;
}

.info-grid-value {
  font-weight: 600;
}

/* Sub-KPI Grid for Tabs 2, 3, 4 */
.op-sub-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

@media (max-width: 1264px) {
  .op-sub-kpi-grid {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}

@media (max-width: 768px) {
  .op-sub-kpi-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

.op-sub-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  min-height: 80px;
}

/* Tab 2: Vehicle Data */
.op-current-vehicle-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.vehicle-img-box {
  background: #F8FAFC;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.vehicle-specs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 768px) {
  .vehicle-specs-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.spec-cell {
  background: #F8FAFC;
  border: 1px solid #E1E7EF;
  border-radius: 10px;
  padding: 10px 14px;
}

/* Tab 3: Documents */
.doc-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

@media (max-width: 1264px) {
  .doc-cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .doc-cards-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}

.op-doc-card {
  background: #FFFFFF;
  border: 1px solid #E1E7EF;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.op-doc-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(8, 44, 105, 0.06);
}

.doc-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #F1F5F9;
}

.doc-card-body {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.doc-card-thumb {
  width: 52px;
  height: 52px;
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.doc-actions-row {
  display: flex;
  align-items: center;
  gap: 10px;
  border-top: 1px solid #F1F5F9;
  padding-top: 10px;
}

.doc-action-btn {
  flex: 1;
  height: 32px;
  border: 1px solid #DFE5EE;
  border-radius: 8px;
  background: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  color: #082C69;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.doc-action-btn:hover:not(:disabled) {
  background: #F8FAFC;
  border-color: #082C69;
}

.doc-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Modal Tables */
.op-modal-table {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #E1E7EF;
  background: #FFFFFF;
}

.op-modal-table table {
  width: 100%;
}

.op-modal-table th {
  background-color: #F8FAFC !important;
  color: #63738A !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  white-space: nowrap;
}

.op-modal-table td {
  font-size: 13px !important;
  color: #142B4A;
}

.op-modal-table tr:hover {
  background-color: #F8FAFC !important;
}
</style>
